import { useEffect, useMemo, useState } from 'react';
import { fetchRegister, normalizeRegisterItems } from '../api';
import {
  CONDITIONS,
  EQUIPMENT_TYPES,
  REGISTER_COLUMNS,
  SHAREPOINT_REGISTER_URL,
  STATUS_FILTERS,
} from '../constants';
import { SelectControl } from '../components/SelectControl';
import { navigate } from '../navigation';
import { buildRegisterCsv, choiceClassName, downloadTextFile, formatDisplayDate, toISODate } from '../utils';

function compareValues(left, right) {
  return String(left || '').localeCompare(String(right || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function FilterSelect({ id, label, value, onChange, options, allLabel }) {
  return (
    <div className="register-filter">
      <label className="field-label" htmlFor={id}>{label}</label>
      <SelectControl
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        options={options}
        placeholder={allLabel}
        className="field-input"
      />
    </div>
  );
}

function ChoicePill({ value, variant = 'choice' }) {
  return (
    <span className={`${variant}-pill is-${choiceClassName(value)}`}>
      <span className="choice-dot" aria-hidden="true" />
      {value || '—'}
    </span>
  );
}

function renderRegisterValue(item, column) {
  const value = item[column.key];

  if (column.key === 'asset_tag') {
    return <span className="register-tag">{value || '—'}</span>;
  }
  if (column.kind === 'status') return <ChoicePill value={value} variant="status" />;
  if (column.kind === 'choice') return <ChoicePill value={value} />;
  if (column.kind === 'date') {
    return formatDisplayDate(value) || <span className="register-muted">—</span>;
  }
  return value || <span className="register-muted">—</span>;
}

export function RegisterPage() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [equipmentType, setEquipmentType] = useState('');
  const [status, setStatus] = useState('');
  const [condition, setCondition] = useState('');
  const [sortKey, setSortKey] = useState('asset_tag');
  const [sortDir, setSortDir] = useState('asc');

  const loadRegister = async (filters = {}) => {
    setIsLoading(true);
    setError('');

    try {
      const data = await fetchRegister(filters);
      setItems(normalizeRegisterItems(data));
    } catch (loadError) {
      console.error('Register load error:', loadError);
      setItems([]);
      setError(loadError.message || 'Could not load the asset register.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRegister();
    // Initial load only. Typing filters the already-fetched rows locally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleItems = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return items
      .filter((item) => {
        if (equipmentType && item.equipment_type !== equipmentType) return false;
        if (status && item.status !== status) return false;
        if (condition && item.condition !== condition) return false;
        if (!needle) return true;

        return Object.values(item).some((value) =>
          String(value || '').toLowerCase().includes(needle)
        );
      })
      .sort((left, right) => {
        const result = compareValues(left[sortKey], right[sortKey]);
        return sortDir === 'asc' ? result : -result;
      });
  }, [items, query, equipmentType, status, condition, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(key);
    setSortDir('asc');
  };

  const openAsset = (assetTag) => {
    if (!assetTag) return;
    navigate(`/?tag=${encodeURIComponent(assetTag)}`);
  };

  const exportCsv = () => {
    if (visibleItems.length === 0) return;
    downloadTextFile(
      `PAC-IT-Asset-Register-${toISODate(new Date())}.csv`,
      buildRegisterCsv(visibleItems, REGISTER_COLUMNS)
    );
  };

  const hasFilters = Boolean(query || equipmentType || status || condition);
  const emptyTitle = items.length === 0 ? 'Nothing to show yet' : 'No matching assets';
  const emptyCopy = items.length === 0
    ? 'No assets were returned from SharePoint.'
    : 'No assets match the current search or filters.';

  return (
    <div className="form-card register-card">
      <div className="register-hero">
        <div className="register-hero-copy">
          <p className="register-eyebrow">Live SharePoint list</p>
          <div className="register-title-row">
            <h2>Asset Register</h2>
            {!isLoading && (
              <span className="register-count" aria-label={`${visibleItems.length} visible assets`}>
                {visibleItems.length}
              </span>
            )}
          </div>
          <p className="register-subtitle">
            Search the live SharePoint list. Click a row to check the asset in or out.
          </p>
        </div>
        <div className="register-actions">
          <a
            href={SHAREPOINT_REGISTER_URL}
            target="_blank"
            rel="noreferrer"
            className="btn btn-secondary"
          >
            Open in SharePoint
          </a>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={exportCsv}
            disabled={isLoading || visibleItems.length === 0}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => loadRegister({
              query,
              equipment_type: equipmentType,
              status,
              condition,
            })}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="register-toolbar">
        <label className="register-search" htmlFor="register-search">
          <span className="field-label">Search</span>
          <span className="register-search-field">
            <svg className="register-search-icon" viewBox="0 0 20 20" aria-hidden="true">
              <circle cx="8.5" cy="8.5" r="5.25" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <path d="M12.4 12.4 17 17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <input
              id="register-search"
              type="search"
              className="field-input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Tag, assignee, project, serial..."
            />
          </span>
        </label>
        <FilterSelect
          id="register-equipment-type"
          label="Equipment Type"
          value={equipmentType}
          onChange={setEquipmentType}
          options={EQUIPMENT_TYPES}
          allLabel="All types"
        />
        <FilterSelect
          id="register-status"
          label="Status"
          value={status}
          onChange={setStatus}
          options={STATUS_FILTERS}
          allLabel="All statuses"
        />
        <FilterSelect
          id="register-condition"
          label="Condition"
          value={condition}
          onChange={setCondition}
          options={CONDITIONS}
          allLabel="All conditions"
        />
      </div>

      <div className="register-meta">
        <span>
          {isLoading
            ? 'Loading assets from SharePoint...'
            : `Showing ${visibleItems.length} of ${items.length} assets`}
        </span>
        {hasFilters && (
          <button
            type="button"
            className="text-button"
            onClick={() => {
              setQuery('');
              setEquipmentType('');
              setStatus('');
              setCondition('');
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="error-box">
          <strong>Could not load the register.</strong>
          <p>{error}</p>
          <p>
            The portal reads this list from SharePoint. Refresh to try again,
            or ask IT to confirm the register connection is configured.
          </p>
        </div>
      )}

      {isLoading && (
        <div className="register-empty">
          <div className="spinner spinner-lg" />
          <div className="loading-text">Loading asset register...</div>
        </div>
      )}

      {!isLoading && visibleItems.length === 0 && !error && (
        <div className="register-empty">
          <div className="register-empty-mark" aria-hidden="true" />
          <strong>{emptyTitle}</strong>
          <p>{emptyCopy}</p>
        </div>
      )}

      {!isLoading && visibleItems.length > 0 && (
        <div className="register-table-wrap">
          <table className="register-table">
            <thead>
              <tr>
                {REGISTER_COLUMNS.map((column) => {
                  const isActive = sortKey === column.key;
                  return (
                    <th
                      key={column.key}
                      scope="col"
                      aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <button
                        type="button"
                        className={`sort-button${isActive ? ' is-active' : ''}`}
                        onClick={() => toggleSort(column.key)}
                      >
                        {column.label}
                        <span
                          className={`sort-caret${isActive ? ` is-${sortDir}` : ''}`}
                          aria-hidden="true"
                        />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item, index) => (
                <tr
                  key={`${item.asset_tag || 'asset'}-${index}`}
                  tabIndex={item.asset_tag ? 0 : undefined}
                  onClick={() => openAsset(item.asset_tag)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openAsset(item.asset_tag);
                    }
                  }}
                >
                  {REGISTER_COLUMNS.map((column) => (
                    <td key={column.key}>
                      {renderRegisterValue(item, column)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
